/**
 * <MyMarketplaceCard /> — Dashboard tile with your marketplace listings +
 * archive / republish / edit actions.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { PackageCheck, Archive, ArchiveRestore, ExternalLink, Loader2 } from "lucide-react";

const STATUS_COLOUR = {
  active: "#16a34a",
  sold: "#0ea5e9",
  hidden: "#f59e0b",
  archived: "#94a3b8",
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
};

export default function MyMarketplaceCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/marketplace/me/listings");
      setItems(data.items || []);
    } catch { /* user has none — hide silently */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const archive = async (l) => {
    if (!window.confirm(`Archive "${l.title}"? It won't be visible until you republish.`)) return;
    setBusyId(l.id);
    try {
      await http.post(`/marketplace/${l.id}/archive`);
      toast.success("Listing archived.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't archive."); }
    finally { setBusyId(null); }
  };
  const republish = async (l) => {
    setBusyId(l.id);
    try {
      const { data } = await http.post(`/marketplace/${l.id}/republish?extend_days=30`);
      toast.success(`Republished — visible for 30 more days (expires ${(data.expires_at || "").slice(0, 10)}).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't republish."); }
    finally { setBusyId(null); }
  };

  if (!loading && items.length === 0) return null;

  return (
    <div data-testid="my-marketplace-card" className="wofy-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>
            <PackageCheck size={12} className="inline -mt-0.5 mr-1" /> YOUR LISTINGS
          </div>
          <h2 className="font-display text-xl mt-1">Your marketplace listings</h2>
        </div>
        <Link to="/marketplace" className="wofy-btn-ghost text-xs">Browse all <ExternalLink className="w-3 h-3" /></Link>
      </div>
      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="space-y-2" data-testid="my-marketplace-list">
          {items.slice(0, 8).map((l) => {
            const clr = STATUS_COLOUR[l.status] || "#64748b";
            const isBusy = busyId === l.id;
            const days = daysUntil(l.expires_at);
            const expiringSoon = l.status === "active" && days !== null && days <= 3;
            return (
              <div key={l.id} className="flex items-center gap-3 rounded-lg border p-3" style={{ borderColor: expiringSoon ? "#f59e0b" : "var(--wofy-line)" }} data-testid={`my-listing-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{l.title}</div>
                  <div className="text-xs mt-0.5 opacity-70">
                    {l.category || "—"} · ${l.price ?? "—"}
                    {l.status === "active" && days !== null && (
                      <span className={expiringSoon ? "text-amber-700 ml-1 font-semibold" : "ml-1"}>
                        · {days > 0 ? `expires in ${days}d` : "expired"}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: `${clr}22`, color: clr }}>
                  {l.status}
                  {l.auto_archived && l.status === "archived" && " · auto"}
                </span>
                <Link to={`/marketplace/${l.id}`} className="wofy-btn-ghost text-xs" data-testid={`my-listing-view-${l.id}`}>
                  <ExternalLink className="w-3 h-3" />
                </Link>
                {l.status !== "archived" ? (
                  <button
                    onClick={() => archive(l)}
                    disabled={isBusy}
                    className="wofy-btn-ghost text-xs"
                    title="Archive so you can edit and republish"
                    data-testid={`my-listing-archive-${l.id}`}
                  >
                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
                  </button>
                ) : (
                  <button
                    onClick={() => republish(l)}
                    disabled={isBusy}
                    className="wofy-btn-ghost text-xs"
                    style={{ color: "var(--wofy-primary)" }}
                    data-testid={`my-listing-republish-${l.id}`}
                  >
                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArchiveRestore className="w-3 h-3" />}
                    Republish · +30d
                  </button>
                )}
              </div>
            );
          })}
          {items.length > 8 && (
            <p className="text-xs text-center pt-2" style={{ color: "var(--wofy-text-soft)" }}>
              + {items.length - 8} more · showing latest 8
            </p>
          )}
        </div>
      )}
    </div>
  );
}
